import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, type ReactNode } from 'react';
import { authApi } from './auth.api';
import { AuthContext, authQueryKey, type AuthStatus } from './auth-context';
import type { PublicUser } from './types';

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: authQueryKey,
    queryFn: authApi.me,
    retry: false,
    staleTime: Infinity,
    // Any failure to load the current user (401, or API unreachable) is treated
    // as "not authenticated" — the app shows the login screen rather than crashing.
  });

  const setUser = useCallback(
    (user: PublicUser) => queryClient.setQueryData(authQueryKey, user),
    [queryClient],
  );

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: authQueryKey });
  }, [queryClient]);

  const signOut = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      queryClient.setQueryData(authQueryKey, null);
      queryClient.clear();
    }
  }, [queryClient]);

  const user = query.data ?? null;
  const status: AuthStatus = query.isLoading
    ? 'loading'
    : user
      ? 'authenticated'
      : 'unauthenticated';

  return (
    <AuthContext.Provider
      value={{
        user,
        status,
        isAuthenticated: status === 'authenticated',
        setUser,
        signOut,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
